import { useState, useEffect, useRef } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowRight, ArrowUpRight, Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  LandingFooter,
  LandingNav,
  certificationPillars,
  PROGRAM_IMG_MAIN,
  testimonials,
} from '@/features/marketing'
import { useTrainings } from '@/features/trainings'
import {
  MARQUEE_LOGOS,
  SERVICES,
  TYPE_META,
  PROGRAM_FEATURES,
  HOW_IT_WORKS,
  BUBBLES,
} from './constants'
import ProgramCardSkeleton from './components/ProgramCardSkeleton'
import ArrowBtn from './components/ArrowBtn'
import CardIcon from './components/CardIcon'
import CertCard from './components/CertCard'
import ServiceCard from './components/ServiceCard'
import ConvBubble from './components/ConvBubble'
import FeaturedResponseBubble from './components/FeaturedResponseBubble'

const HOW_STEP_KEYS = ['diagnose', 'deploy', 'operate'] as const

function getDurationWeeks(start: string | null, end: string | null): number | null {
  if (!start || !end) return null
  const weeks = Math.round(
    (new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24 * 7),
  )
  return weeks > 0 ? weeks : null
}

export default function HomePage() {
  const { t } = useTranslation()
  const heroVideoRef = useRef<HTMLVideoElement>(null)
  const [reduceMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )
  const { data: trainings, isLoading: trainingsLoading } = useTrainings()
  const programmes = (trainings ?? []).slice(0, 4)

  useEffect(() => {
    const el = heroVideoRef.current
    if (!el || reduceMotion) return
    el.play().catch(() => {})
  }, [reduceMotion])

  return (
    <div className="min-h-screen font-body bg-[#08080f] text-white antialiased overflow-x-hidden">
      <LandingNav />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section
        id="top"
        className="relative min-h-screen flex items-center pt-20 overflow-hidden"
        aria-label="Hero"
      >
        {/* ambient corner orbs */}
        <div
          className="pointer-events-none absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full z-[1]"
          style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.22) 0%, transparent 65%)', filter: 'blur(40px)' }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full z-[1]"
          style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 65%)', filter: 'blur(40px)' }}
          aria-hidden
        />

        {!reduceMotion && (
          <video
            ref={heroVideoRef}
            className="absolute inset-0 z-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            poster="/images/hero-fallback-frame.jpg"
            aria-hidden
          >
            <source src="/hero-background-video.mp4" type="video/mp4" />
          </video>
        )}
        {reduceMotion && <div className="absolute inset-0 z-0 bg-[#08080f]" aria-hidden />}
        <div className="pointer-events-none absolute inset-0 z-[1] bg-[#08080f]/75" aria-hidden />

        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 sm:px-10 flex flex-col items-center text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7c3aed]/30 bg-[#7c3aed]/8 backdrop-blur-md mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4cd7f6] shrink-0" style={{ boxShadow: '0 0 8px rgba(76,215,246,0.8)' }} />
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/70 font-headline">
              {t('home.hero.eyebrow')}
            </span>
          </div>

          <h1 className="font-headline text-5xl sm:text-6xl md:text-[72px] font-bold leading-[1.08] tracking-[-0.025em] text-white mb-6 max-w-4xl">
            {t('home.hero.title1')}{' '}
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#a78bfa] via-[#7c3aed] to-[#4cd7f6]">
              {t('home.hero.title2')}
            </span>
          </h1>

          <p className="text-lg leading-relaxed text-white/55 max-w-2xl mb-12">
            {t('home.hero.desc')}{' '}
            <span className="text-white/80">{t('home.hero.descHighlight')}</span>
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-4">
            <Link
              to="/programmes"
              className="px-8 py-4 bg-[#7c3aed] text-white font-headline text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 rounded-xl transition-all hover:bg-[#6d28d9] hover:shadow-[0_0_32px_rgba(124,58,237,0.6)] active:scale-[0.98]"
            >
              {t('home.hero.cta1')}
              <ArrowRight className="w-4 h-4 shrink-0" />
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 border border-white/50 text-white font-headline text-sm font-bold uppercase tracking-wider rounded-xl hover:border-white hover:bg-white/8 transition-all"
            >
              {t('home.hero.cta2')}
            </Link>
          </div>
        </div>
      </section>

      

      {/* ── CONVERSATIONS BOARD ────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden py-24 sm:py-32"
        style={{
          background: [
            'radial-gradient(ellipse 70% 50% at 20% 20%, rgba(168,85,247,0.35), transparent 55%)',
            'radial-gradient(ellipse 60% 40% at 85% 70%, rgba(124,58,237,0.4), transparent 55%)',
            'radial-gradient(ellipse 40% 30% at 50% 100%, rgba(76,215,246,0.15), transparent 60%)',
            '#050509',
          ].join(', '),
        }}
        aria-labelledby="conv-heading"
      >
        {/* header */}
        <div className="max-w-[1280px] mx-auto px-6 sm:px-10 text-center mb-16 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/[0.12] bg-white/[0.04] backdrop-blur-md mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4cd7f6] shrink-0" style={{ boxShadow: '0 0 6px rgba(76,215,246,0.8)' }} />
            <span className="font-headline text-[11px] font-bold uppercase tracking-[0.16em] text-white/60">
              {t('home.trustGap.eyebrow')}
            </span>
          </div>
          <h2
            id="conv-heading"
            className="font-headline text-4xl sm:text-5xl md:text-[56px] font-bold leading-[1.1] text-white mb-5 max-w-3xl mx-auto"
          >
            {t('home.trustGap.title1')}
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#c4b5fd] via-[#a78bfa] to-[#4cd7f6]">
              {t('home.trustGap.title2')}
            </span>
          </h2>
          <p className="text-white/60 text-lg max-w-xl mx-auto leading-relaxed">
            {t('home.trustGap.desc')}
          </p>
        </div>

        {/* bubble board — desktop */}
        <div className="max-w-[1280px] mx-auto px-6 sm:px-10 relative z-10">
          <div className="relative hidden lg:block" style={{ minHeight: '780px' }}>
            {BUBBLES.map((b) => (
              <div
                key={b.id}
                className="absolute w-[260px]"
                style={{
                  top: b.pos.top,
                  left: b.pos.left,
                  animation: 'floatBubble 6s ease-in-out infinite',
                  animationDelay: b.animDelay,
                }}
              >
                <div style={{ transform: `rotate(${b.rotate})` }}>
                  <ConvBubble
                    name={b.name}
                    role={t(`home.bubbles.${b.id}.role`)}
                    tag={t(`home.bubbles.${b.id}.tag`)}
                    quote={t(`home.bubbles.${b.id}.quote`)}
                    variant={b.variant}
                  />
                </div>
              </div>
            ))}
            {/* featured center — Skills Up response */}
            <div
              className="absolute w-[300px]"
              style={{
                top: '33%',
                left: 'calc(50% - 150px)',
                animation: 'floatBubble 7s ease-in-out infinite',
                animationDelay: '1.8s',
              }}
            >
              <FeaturedResponseBubble />
            </div>
          </div>

          {/* bubble board — mobile */}
          <div className="flex flex-col gap-4 lg:hidden mb-8">
            {BUBBLES.slice(0, 3).map((b) => (
              <ConvBubble
                key={b.id}
                name={b.name}
                role={t(`home.bubbles.${b.id}.role`)}
                tag={t(`home.bubbles.${b.id}.tag`)}
                quote={t(`home.bubbles.${b.id}.quote`)}
                variant={b.variant}
              />
            ))}
            <FeaturedResponseBubble />
          </div>
        </div>

        {/* persona CTAs */}
        <div className="max-w-[1280px] mx-auto px-6 sm:px-10 mt-8 md:mt-16 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link
              to="/programmes"
              className="group flex items-center gap-5 rounded-[22px] border border-white/[0.10] bg-white/[0.03] backdrop-blur-md p-6 hover:border-[#7c3aed]/50 hover:bg-[#7c3aed]/5 hover:-translate-y-0.5 transition-all duration-300"
            >
              <CardIcon name="badge" />
              <div className="flex-1">
                <p className="font-headline font-bold text-white text-base">{t('home.trustGap.persona1Title')}</p>
                <p className="text-white/60 text-sm mt-0.5">{t('home.trustGap.persona1Desc')}</p>
              </div>
              <ArrowBtn />
            </Link>
            <Link
              to="/contact"
              className="group flex items-center gap-5 rounded-[22px] border border-white/[0.10] bg-white/[0.03] backdrop-blur-md p-6 hover:border-[#7c3aed]/50 hover:bg-[#7c3aed]/5 hover:-translate-y-0.5 transition-all duration-300"
            >
              <CardIcon name="store" />
              <div className="flex-1">
                <p className="font-headline font-bold text-white text-base">{t('home.trustGap.persona2Title')}</p>
                <p className="text-white/60 text-sm mt-0.5">{t('home.trustGap.persona2Desc')}</p>
              </div>
              <ArrowBtn />
            </Link>
          </div>
        </div>
      </section>

      {/* ── PARTNERS MARQUEE ─────────────────────────────────────────────── */}
      <section className="overflow-hidden bg-[#08080f] py-12" aria-label="Partners">
        <p className="text-center font-headline text-[11px] font-bold uppercase tracking-[0.24em] text-white/25 mb-10">
          {t('home.partners.label')}
        </p>
        <div
          className="flex items-center"
          style={{ animation: 'marquee 22s linear infinite', width: 'max-content' }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.animationPlayState = 'paused')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.animationPlayState = 'running')}
        >
          {MARQUEE_LOGOS.map((p, i) => (
            <div
              key={`${p.id}-${i}`}
              className="shrink-0 flex items-center justify-center overflow-hidden"
              style={{ width: '160px', height: '64px', padding: '0 16px' }}
            >
              <img
                src={p.logo}
                alt={p.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  transform: `scale(${p.scale})`,
                  filter: 'grayscale(100%)',
                  opacity: 0.5,
                  transition: 'opacity 0.3s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.75' }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5' }}
              />
            </div>
          ))}
        </div>
      </section>

      {/* ── SERVICES ─────────────────────────────────────────────────────── */}
      <section className="relative max-w-[1280px] mx-auto px-6 sm:px-10 py-20 sm:py-24 overflow-hidden" aria-labelledby="services-heading">
        {/* Ambient orbs */}
        <div className="pointer-events-none absolute inset-0" aria-hidden>
          <div className="absolute top-0 left-1/4 w-[520px] h-[520px] rounded-full blur-[130px] bg-purple-700/10" />
          <div className="absolute bottom-0 right-1/4 w-[420px] h-[420px] rounded-full blur-[110px] bg-cyan-500/10" />
        </div>

        <div className="relative z-10 text-center mb-14">
          <p className="font-headline text-[11px] font-bold uppercase tracking-[0.22em] text-[#7c3aed] mb-3">
            {t('home.services.eyebrow')}
          </p>
          <h2
            id="services-heading"
            className="font-headline text-3xl sm:text-4xl font-bold text-white"
          >
            {t('home.services.title')}
          </h2>
          <p className="mt-4 text-white/60 max-w-xl mx-auto">
            {t('home.services.desc')}
          </p>
        </div>

        <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((service) => (
            <ServiceCard
              key={service.id}
              service={{
                ...service,
                title: t(`home.services.${service.id}.title`),
                subtitle: t(`home.services.${service.id}.subtitle`),
                description: t(`home.services.${service.id}.description`),
                items: (t(`home.services.${service.id}.items`, { returnObjects: true }) as string[]),
              }}
            />
          ))}
        </div>
      </section>

      {/* ── PROGRAMS + FEATURES 2×3 GRID ────────────────────────────────── */}
      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 pb-20 sm:pb-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
          <div>
            <p className="font-headline text-[11px] font-bold uppercase tracking-[0.22em] text-[#7c3aed] mb-3">
              {t('home.programs.eyebrow')}
            </p>
            <h2 className="font-headline text-3xl sm:text-4xl font-bold text-white">
              {t('home.programs.title')}
            </h2>
            <p className="mt-3 text-white/60 max-w-xl">{t('home.programs.desc')}</p>
          </div>
          <Link to="/programmes" className="hidden sm:inline-flex items-center gap-2 font-headline text-sm font-semibold text-[#a78bfa] hover:text-white transition-colors shrink-0">
            {t('home.programs.viewAll')} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* row 1 — programs (second card featured) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
          {trainingsLoading ? (
            <>
              <ProgramCardSkeleton />
              <ProgramCardSkeleton featured />
              <ProgramCardSkeleton />
              <ProgramCardSkeleton />
            </>
          ) : programmes.map((training, index) => {
            const featured = index === 1
            const meta = TYPE_META[training.type]
            const durationWeeks = getDurationWeeks(training.start_date, training.end_date)
            const image = training.thumbnail_url ?? PROGRAM_IMG_MAIN

            return featured ? (
              /* ── featured card ── */
              <Link
                key={training.id}
                to="/programmes/$trainingId"
                params={{ trainingId: training.id }}
                className="group relative bg-[#7c3aed] rounded-2xl p-7 overflow-hidden md:-translate-y-3 flex flex-col cursor-pointer"
                style={{ boxShadow: '0 0 60px rgba(124,58,237,0.5)' }}
              >
                <div className="pointer-events-none absolute top-0 right-0 w-56 h-56 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4 blur-2xl" />
                {training.thumbnail_url && (
                  <div className="relative z-10 rounded-xl overflow-hidden mb-5 aspect-video">
                    <img src={image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex items-start justify-between mb-6 relative z-10">
                  <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center text-white shrink-0">
                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>{meta.icon}</span>
                  </div>
                  <div className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center text-white/60 group-hover:text-white group-hover:border-white/60 transition-all shrink-0">
                    <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
                <span className="px-2 py-0.5 bg-white/20 text-white font-headline text-[10px] font-bold uppercase tracking-wide rounded w-fit mb-2 relative z-10">
                  {meta.badge}
                </span>
                <h3 className="font-headline text-xl font-bold text-white mb-2 relative z-10">{training.title}</h3>
                {training.description && (
                  <p className="text-white/75 text-sm leading-relaxed flex-1 relative z-10">{training.description}</p>
                )}
                {durationWeeks && (
                  <div className="mt-6 flex items-center gap-4 text-white/60 text-xs font-headline relative z-10">
                    <span>{t('home.week', { count: durationWeeks })}</span>
                  </div>
                )}
                <span className="mt-5 inline-flex items-center gap-2 font-headline text-sm font-bold text-white relative z-10">
                  {t('home.programs.joinCohort')} <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ) : (
              /* ── standard card ── */
              <Link
                key={training.id}
                to="/programmes/$trainingId"
                params={{ trainingId: training.id }}
                className="group relative bg-[#0f0f1a] rounded-2xl border border-white/[0.07] p-7 hover:border-[#7c3aed]/40 transition-all duration-300 overflow-hidden flex flex-col cursor-pointer"
              >
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#7c3aed]/4 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                {training.thumbnail_url && (
                  <div className="relative z-10 rounded-xl overflow-hidden mb-5 aspect-video">
                    <img src={image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex items-start justify-between mb-6 relative z-10">
                  <CardIcon name={meta.icon} />
                  <ArrowBtn />
                </div>
                <span className="px-2 py-0.5 bg-[#7c3aed]/15 text-[#a78bfa] font-headline text-[10px] font-bold uppercase tracking-wide rounded w-fit mb-2 relative z-10">
                  {meta.badge}
                </span>
                <h3 className="font-headline text-xl font-bold text-white mb-2 group-hover:text-[#d2bbff] transition-colors relative z-10">{training.title}</h3>
                {training.description && (
                  <p className="text-white/60 text-sm leading-relaxed flex-1 relative z-10">{training.description}</p>
                )}
                {durationWeeks && (
                  <div className="mt-5 flex items-center gap-4 text-white/30 text-xs font-headline relative z-10">
                    <span>{t('home.week', { count: durationWeeks })}</span>
                  </div>
                )}
                <span className="mt-5 inline-flex items-center gap-2 font-headline text-sm font-semibold text-[#a78bfa] group-hover:text-white transition-colors relative z-10">
                  {t('home.programs.learnMore')} <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            )
          })}
        </div>

        {/* row 2 — differentiators */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PROGRAM_FEATURES.map((f) => (
            <div
              key={f.id}
              className="group bg-[#0f0f1a] rounded-2xl border border-white/[0.07] p-7 hover:border-[#7c3aed]/30 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-5">
                <CardIcon name={f.icon} />
                <ArrowBtn />
              </div>
              <h3 className="font-headline text-lg font-bold text-white mb-2">{t(`home.programs.features.${f.id}.title`)}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{t(`home.programs.features.${f.id}.desc`)}</p>
            </div>
          ))}
        </div>

        <div className="sm:hidden text-center mt-8">
          <Link to="/programmes" className="inline-flex items-center gap-2 font-headline text-sm font-semibold text-[#a78bfa] hover:text-white transition-colors">
            {t('home.programs.viewAllMobile')} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ── HOW IT WORKS — purple gradient numbered section ────────────── */}
      <section
        className="py-24 relative overflow-hidden"
        aria-labelledby="how-heading"
        style={{ background: 'linear-gradient(160deg, #0d0618 0%, #1e0a50 45%, #0d0618 100%)' }}
      >
        {/* ambient glow */}
        <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(124,58,237,0.2) 0%, transparent 70%)' }} aria-hidden />

        <div className="max-w-[1280px] mx-auto px-6 sm:px-10 relative z-10">
          <div className="text-center mb-14">
            <p className="font-headline text-[11px] font-bold uppercase tracking-[0.22em] text-[#a78bfa]/70 mb-3">{t('home.howItWorks.eyebrow')}</p>
            <h2 id="how-heading" className="font-headline text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('home.howItWorks.title')}
            </h2>
            <p className="text-white/60 max-w-xl mx-auto">
              {t('home.howItWorks.desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {HOW_IT_WORKS.map(({ step }, index) => {
              const key = HOW_STEP_KEYS[index]
              return (
                <div
                  key={step}
                  className="group relative bg-[#08080f]/70 backdrop-blur-sm rounded-2xl border border-white/[0.08] p-8 hover:border-white/[0.15] transition-all duration-300 overflow-hidden"
                >
                  <div className="flex items-start justify-between mb-5">
                    <span className="font-headline text-6xl sm:text-7xl font-bold text-white/[0.08] leading-none group-hover:text-white/[0.13] transition-colors">
                      {step}
                    </span>
                    <div className="w-8 h-8 rounded-full border border-white/[0.15] flex items-center justify-center text-white/30 group-hover:border-white/30 group-hover:text-white/60 transition-all shrink-0 mt-1">
                      <ArrowUpRight className="w-4 h-4" />
                    </div>
                  </div>
                  <h3 className="font-headline text-xl font-bold text-white mb-3">{t(`home.howItWorks.steps.${key}.title`)}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{t(`home.howItWorks.steps.${key}.desc`)}</p>
                </div>
              )
            })}
          </div>

          {/* format note */}
          <div className="mt-8 flex items-center justify-center gap-3 py-4 px-6 bg-white/[0.04] border border-white/[0.07] rounded-xl w-fit mx-auto">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4cd7f6]" style={{ boxShadow: '0 0 6px rgba(76,215,246,0.7)' }} />
            <p className="font-headline text-sm text-white/80 font-semibold">
              {t('home.howItWorks.pillars')}
            </p>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ─────────────────────────────────────────────────── */}
      <section className="py-20 sm:py-24 overflow-hidden" aria-labelledby="testimonials-heading">
        <div className="max-w-[1280px] mx-auto px-6 sm:px-10 mb-12">
          <div className="text-center">
            <p className="font-headline text-[11px] font-bold uppercase tracking-[0.22em] text-[#7c3aed] mb-3">{t('home.testimonials.eyebrow')}</p>
            <h2 id="testimonials-heading" className="font-headline text-3xl sm:text-4xl font-bold text-white">
              {t('home.testimonials.title')}
            </h2>
          </div>
        </div>

        {/* horizontal scroll — intentional overflow for visual peek */}
        <div
          className="pl-6 sm:pl-10 overflow-x-auto pb-6 scrollbar-none"
          style={{
            scrollbarWidth: 'none',
            WebkitMaskImage: 'linear-gradient(to right, black 0%, black 78%, transparent 100%)',
            maskImage: 'linear-gradient(to right, black 0%, black 78%, transparent 100%)',
          }}
        >
          <div className="flex gap-5" style={{ width: 'max-content', paddingRight: '1.5rem' }}>
            {testimonials.map((testimonial, index) => {
              const ti = t(`home.testimonials.items.${index}`, { returnObjects: true }) as { role: string; company: string; quote: string }
              return (
                <div key={testimonial.name} className="relative pt-8 w-72 flex-shrink-0">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
                    <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#7c3aed]" style={{ boxShadow: '0 0 20px rgba(124,58,237,0.45)' }}>
                      <img src={testimonial.avatar} alt={testimonial.name} className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <div
                    className="relative rounded-2xl border border-[#7c3aed]/20 pt-12 px-6 pb-8"
                    style={{ background: 'linear-gradient(160deg, #1a0a3e 0%, #0d0618 100%)' }}
                  >
                    <div className="flex gap-0.5 justify-center mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>
                    <p className="font-headline text-sm font-bold text-white text-center">{testimonial.name}</p>
                    <p className="text-white/55 text-xs text-center font-headline mt-0.5 mb-5">
                      {ti.role} · {ti.company}
                    </p>
                    <p className="text-white/60 text-sm leading-relaxed">"{ti.quote}"</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── CERTIFICATIONS ───────────────────────────────────────────────── */}
      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 pb-20 sm:pb-24">
        {(() => {
          const translatedPillars = t('home.certs.pillars', { returnObjects: true }) as Array<{ label: string; title: string; body: string; advantageTitle: string; advantageBody: string; bullets?: string[] }>
          const mergedPillars = certificationPillars.map((pillar, i) => ({
            ...pillar,
            label: translatedPillars[i]?.label || pillar.label,
            title: translatedPillars[i]?.title || pillar.title,
            body: translatedPillars[i]?.body || pillar.body,
            advantageTitle: translatedPillars[i]?.advantageTitle || pillar.advantageTitle,
            advantageBody: translatedPillars[i]?.advantageBody || pillar.advantageBody,
            bullets: translatedPillars[i]?.bullets || pillar.bullets,
          }))

          return (
            <>
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
                <div>
                  <p className="font-headline text-[11px] font-bold uppercase tracking-[0.22em] text-[#7c3aed] mb-3">
                    {t('home.certs.eyebrow')}
                  </p>
                  <h2 className="font-headline text-3xl sm:text-4xl font-bold text-white">
                    {t('home.certs.title')}
                  </h2>
                  <p className="mt-3 text-white/60 max-w-xl">
                    {t('home.certs.desc')}
                  </p>
                </div>
                <Link
                  to="/certifications"
                  className="hidden sm:inline-flex items-center gap-2 font-headline text-sm font-semibold text-[#a78bfa] hover:text-white transition-colors shrink-0"
                >
                  {t('home.certs.cta')} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Asymmetric grid: 1 tall + 2 stacked */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:auto-rows-[260px]">
                {/* TALL CARD — spans 2 rows */}
                <CertCard pillar={mergedPillars[0]} className="md:row-span-2" tall />

                {/* TWO STACKED CARDS */}
                <CertCard pillar={mergedPillars[1]} />
                <CertCard pillar={mergedPillars[2]} />
              </div>
            </>
          )
        })()}
      </section>

      {/* ── CALL TO ACTION ───────────────────────────────────────────────── */}
      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 pb-20 sm:pb-24">
        <div
          className="relative overflow-hidden rounded-2xl p-12 sm:p-16 md:p-20 text-center"
          style={{
            background: 'linear-gradient(135deg, #1a0a3e 0%, #3b1fa8 50%, #1a0a3e 100%)',
            boxShadow: '0 0 80px rgba(124,58,237,0.3)',
          }}
        >
          <div className="pointer-events-none absolute -top-24 -left-24 w-64 h-64 bg-[#7c3aed]/30 rounded-full blur-[80px]" />
          <div className="pointer-events-none absolute -bottom-24 -right-24 w-64 h-64 bg-[#4cd7f6]/15 rounded-full blur-[80px]" />
          <div
            className="pointer-events-none absolute inset-0 opacity-20 rounded-2xl"
            style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.25) 1px, transparent 1px)', backgroundSize: '28px 28px' }}
          />

          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/20 bg-white/5 backdrop-blur-sm rounded-full mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#4cd7f6]" style={{ boxShadow: '0 0 6px rgba(76,215,246,0.8)' }} />
              <span className="font-headline text-[10px] font-bold uppercase tracking-[0.18em] text-white/70">
                {t('home.cta.badge')}
              </span>
            </div>

            <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight mb-5 max-w-2xl mx-auto">
              {t('home.cta.title')}
            </h2>
            <p className="text-white/60 text-lg leading-relaxed max-w-xl mx-auto mb-10">
              {t('home.cta.desc')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/signup"
                className="px-8 py-3.5 bg-white text-[#3b1fa8] font-headline font-bold text-sm uppercase tracking-wider rounded-xl inline-flex items-center gap-2 hover:bg-white/90 transition-all active:scale-[0.98]"
              >
                {t('home.cta.primary')} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="px-8 py-3.5 border border-white/30 text-white font-headline font-bold text-sm uppercase tracking-wider rounded-xl hover:bg-white/10 transition-all"
              >
                {t('home.cta.secondary')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  )
}
